import {
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
} from "react";
import { LayoutGrid, Search, X } from "lucide-react";
import { Badge, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { palette, radius, space, typography } from "@ux/tokens";
import { useWorkspaces } from "../../admin/workspaces/hooks";
import type { WorkspaceView } from "../../admin/workspaces/types";

export interface WorkspacePickerProps {
    readonly id?: string;
    readonly value: WorkspaceView | null;
    readonly onChange: (workspace: WorkspaceView | null) => void;
    readonly placeholder?: string;
    readonly disabled?: boolean;
    readonly invalid?: boolean;
    /**
     * When true, each result row decorates workspaces that already have
     * an open occupant assignment with a small "Occupied" badge. Used by
     * the workspace-occupants flow so the operator can spot displacement
     * before picking. Off by default — irrelevant for asset-to-workspace
     * assignments and for inventory mode.
     */
    readonly showOccupiedHint?: boolean;
}

/**
 * Typeahead workspace picker. Visual cousin of `UserPicker` — same
 * empty/picked surfaces, same outside-click handling, same mousedown
 * trick to register selection before blur cancels the dropdown — but
 * backed by `useWorkspaces` so it stays in lockstep with the workspaces
 * admin's own filtering rules (active-only by default, name-prefix
 * search).
 *
 * Used by `AssetForm`'s optional inline "Assign to" section so a user
 * creating an asset on the standalone Assets page can pin it to a
 * workspace in one round-trip via the atomic `add-asset-to-workspace`
 * endpoint.
 */
export function WorkspacePicker({
    id,
    value,
    onChange,
    placeholder = "Search by name or number…",
    disabled = false,
    invalid = false,
    showOccupiedHint = false,
}: WorkspacePickerProps): ReactElement {
    if (value !== null) {
        return (
            <SelectedWorkspaceCard
                workspace={value}
                onClear={() => onChange(null)}
                disabled={disabled}
            />
        );
    }

    return (
        <WorkspaceSearchField
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            invalid={invalid}
            showOccupiedHint={showOccupiedHint}
            onPick={onChange}
        />
    );
}

interface WorkspaceSearchFieldProps {
    readonly id: string | undefined;
    readonly placeholder: string;
    readonly disabled: boolean;
    readonly invalid: boolean;
    readonly showOccupiedHint: boolean;
    readonly onPick: (workspace: WorkspaceView) => void;
}

function WorkspaceSearchField({
    id,
    placeholder,
    disabled,
    invalid,
    showOccupiedHint,
    onPick,
}: WorkspaceSearchFieldProps): ReactElement {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const search = useWorkspaces({
        q: query.trim(),
        sageOfficeId: "",
        type: "",
        includeDeleted: false,
    });

    useEffect(() => {
        function handleDocumentMouseDown(event: MouseEvent): void {
            if (containerRef.current === null) return;
            if (event.target instanceof Node && containerRef.current.contains(event.target)) {
                return;
            }
            setOpen(false);
        }
        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
    }, []);

    const trimmed = query.trim();
    const showDropdown = open;
    const isFetching = search.isFetching;
    // Trim the displayed list so the dropdown stays usable in tenants
    // with hundreds of workspaces — typing tightens the result anyway.
    const results = (search.data ?? []).slice(0, 25);

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <Stack direction="row" gap={2} align="center">
                <Search size={14} style={{ opacity: 0.55, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                    <TextInput
                        id={id}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        disabled={disabled}
                        invalid={invalid}
                        autoComplete="off"
                    />
                </div>
            </Stack>

            {showDropdown ? (
                <WorkspaceResultsDropdown
                    isFetching={isFetching}
                    isError={search.isError}
                    results={results}
                    queryGiven={trimmed.length > 0}
                    showOccupiedHint={showOccupiedHint}
                    onPick={(w) => {
                        onPick(w);
                        setOpen(false);
                        setQuery("");
                    }}
                />
            ) : null}
        </div>
    );
}

interface WorkspaceResultsDropdownProps {
    readonly isFetching: boolean;
    readonly isError: boolean;
    readonly results: readonly WorkspaceView[];
    readonly queryGiven: boolean;
    readonly showOccupiedHint: boolean;
    readonly onPick: (workspace: WorkspaceView) => void;
}

function WorkspaceResultsDropdown({
    isFetching,
    isError,
    results,
    queryGiven,
    showOccupiedHint,
    onPick,
}: WorkspaceResultsDropdownProps): ReactElement {
    const containerStyle: CSSProperties = {
        position: "absolute",
        top: "calc(100% + 4px)",
        left: 0,
        right: 0,
        zIndex: 40,
        background: palette.canvas,
        border: `1px solid ${palette.border}`,
        borderRadius: radius.md,
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
        maxHeight: 320,
        overflowY: "auto",
    };

    if (isError) {
        return (
            <div style={containerStyle}>
                <div style={{ padding: space[3] }}>
                    <Text role="muted">Search failed. Try again.</Text>
                </div>
            </div>
        );
    }

    if (isFetching && results.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={{ padding: space[3] }}>
                    <Text role="muted">Searching…</Text>
                </div>
            </div>
        );
    }

    if (results.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={{ padding: space[3] }}>
                    <Text role="muted">
                        {queryGiven ? "No matches." : "Start typing a workspace name."}
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle} role="listbox">
            {results.map((w) => (
                <WorkspaceResultRow
                    key={w.id}
                    workspace={w}
                    showOccupiedHint={showOccupiedHint}
                    onPick={onPick}
                />
            ))}
        </div>
    );
}

interface WorkspaceResultRowProps {
    readonly workspace: WorkspaceView;
    readonly showOccupiedHint: boolean;
    readonly onPick: (workspace: WorkspaceView) => void;
}

function WorkspaceResultRow({
    workspace,
    showOccupiedHint,
    onPick,
}: WorkspaceResultRowProps): ReactElement {
    const [hover, setHover] = useState(false);

    const rowStyle: CSSProperties = {
        padding: `${space[2]} ${space[3]}`,
        cursor: "pointer",
        background: hover ? palette.surfaceRaised : "transparent",
        borderBottom: `1px solid ${palette.border}`,
    };

    const occupied =
        showOccupiedHint &&
        workspace.currentAssignmentId !== null &&
        workspace.currentAssignmentId !== undefined &&
        workspace.currentAssignmentId !== "";

    return (
        <div
            role="option"
            aria-selected={false}
            style={rowStyle}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onMouseDown={(e) => {
                e.preventDefault();
                onPick(workspace);
            }}
        >
            <Stack gap={1}>
                <Stack direction="row" gap={2} align="center">
                    <Text role="body" weight="semibold">
                        {workspace.name}
                    </Text>
                    {occupied ? <Badge tone="neutral">Occupied</Badge> : null}
                </Stack>
                <Text role="muted">
                    {workspace.friendlyName !== null && workspace.friendlyName !== undefined
                        ? `${workspace.type} · ${workspace.friendlyName}`
                        : workspace.type}
                </Text>
                <Text
                    role="mono"
                    style={{ fontSize: typography.size.xs, color: palette.fgSubtle }}
                >
                    {workspace.id}
                </Text>
            </Stack>
        </div>
    );
}

interface SelectedWorkspaceCardProps {
    readonly workspace: WorkspaceView;
    readonly onClear: () => void;
    readonly disabled: boolean;
}

function SelectedWorkspaceCard({
    workspace,
    onClear,
    disabled,
}: SelectedWorkspaceCardProps): ReactElement {
    const clearStyle: CSSProperties = {
        background: "transparent",
        border: "none",
        color: palette.fgMuted,
        cursor: disabled ? "not-allowed" : "pointer",
        padding: space[1],
        borderRadius: radius.sm,
        display: "flex",
        alignItems: "center",
        gap: space[1],
    };

    return (
        <Surface tone="raised" padding={3}>
            <Stack direction="row" gap={3} align="center" justify="space-between">
                <Stack direction="row" gap={2} align="center">
                    <LayoutGrid size={18} style={{ color: palette.accent }} />
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">
                            {workspace.name}
                        </Text>
                        <Text role="muted">{workspace.type}</Text>
                        <Text
                            role="mono"
                            style={{ fontSize: typography.size.xs, color: palette.fgSubtle }}
                        >
                            {workspace.id}
                        </Text>
                    </Stack>
                </Stack>
                <button
                    type="button"
                    onClick={onClear}
                    disabled={disabled}
                    style={clearStyle}
                    aria-label="Change workspace"
                >
                    <X size={14} />
                    <span style={{ fontSize: typography.size.sm }}>Change</span>
                </button>
            </Stack>
        </Surface>
    );
}
